import { normalize } from 'normalizr';

import type { PrintPageViewerData } from '@frontend/api';

import { ActionWithPayload } from 'store/types';

import type { RawViewerPartFile, ViewerPartFile } from '../types';
import { partFileSchema, printPageSchema } from './entities';

export const types = {
  RECEIVE_ACTIVE_PART_FILE_UUID: 'common/viewer/RECEIVE_ACTIVE_PART_FILE_UUID',
  RECEIVE_PAGE_VECTORIZATION: 'common/viewer/RECEIVE_PAGE_VECTORIZATION',
  RECEIVE_PART_FILE: 'common/viewer/RECEIVE_PART_FILE',
  RECEIVE_PRINT_PAGE: 'common/viewer/RECEIVE_PRINT_PAGE',
  RECEIVE_PART_FILES: 'common/viewer/RECEIVE_PART_FILES',
  RESET: 'common/viewer/RESET',
  SET_LEGACY_DXF_VIEWER_FOR_PART_FILE:
    'common/viewer/SET_LEGACY_DXF_VIEWER_FOR_PART_FILE',
} as const;

export type ReceiveActivePartFileUuidPayload = {
  partFileUuid: string;
  shouldFocus: boolean;
  shouldPropagateVisibility: boolean;
  ignoreQueue?: boolean;
};

export type ReceivePageVectorizationPayload = {
  pageIdToVectorization: Record<string, unknown>;
};

export type ReceivePartFilePayload = {
  file: RawViewerPartFile;
};

export type ReceivePrintPagePayload = {
  printPage: PrintPageViewerData;
};

export type SetLegacyDxfViewerForPartFilePayload = {
  partFileUuid: string;
  isLegacyDxfViewer: boolean;
};

export type ReceivePartFilesInput = {
  partFiles: RawViewerPartFile[];
};

export type ReceivePartFilesPayload = {
  entities: {
    partFiles: Record<string, ViewerPartFile>;
    printPages?: Record<string, PrintPageViewerData>;
  };
  result: string[];
};

export type ViewerAction =
  | ActionWithPayload<
      typeof types.RECEIVE_ACTIVE_PART_FILE_UUID,
      ReceiveActivePartFileUuidPayload
    >
  | ActionWithPayload<
      typeof types.RECEIVE_PAGE_VECTORIZATION,
      ReceivePageVectorizationPayload
    >
  | ActionWithPayload<typeof types.RECEIVE_PART_FILES, ReceivePartFilesPayload>
  | ActionWithPayload<typeof types.RECEIVE_PART_FILE, ReceivePartFilePayload>
  | ActionWithPayload<typeof types.RECEIVE_PRINT_PAGE, ReceivePrintPagePayload>
  | ActionWithPayload<typeof types.RESET>
  | ActionWithPayload<
      typeof types.SET_LEGACY_DXF_VIEWER_FOR_PART_FILE,
      SetLegacyDxfViewerForPartFilePayload
    >;

export const viewer = {
  receiveActivePartFileUuid: ({
    partFileUuid,
    shouldFocus,
    shouldPropagateVisibility,
    ignoreQueue,
  }: ReceiveActivePartFileUuidPayload) => ({
    type: types.RECEIVE_ACTIVE_PART_FILE_UUID,
    payload: {
      partFileUuid,
      shouldFocus,
      shouldPropagateVisibility,
      ignoreQueue,
    },
  }),
  receivePageVectorization: ({
    pageIdToVectorization,
  }: ReceivePageVectorizationPayload) => ({
    type: types.RECEIVE_PAGE_VECTORIZATION,
    payload: { pageIdToVectorization },
  }),
  receivePartFiles: ({ partFiles }: ReceivePartFilesInput) => ({
    type: types.RECEIVE_PART_FILES,
    payload: normalize<
      ViewerPartFile,
      ReceivePartFilesPayload['entities'],
      ReceivePartFilesPayload['result']
    >(partFiles, [partFileSchema]),
  }),
  receivePartFile: ({ file }: ReceivePartFilePayload) => ({
    type: types.RECEIVE_PART_FILE,
    payload: {
      ...normalize<ViewerPartFile>(file, partFileSchema),
    },
  }),
  receivePrintPage: ({ printPage }: ReceivePrintPagePayload) => ({
    type: types.RECEIVE_PRINT_PAGE,
    payload: {
      ...normalize(printPage, printPageSchema),
    },
  }),
  reset: () => ({
    type: types.RESET,
  }),
  setLegacyDxfViewerForPartFile: ({
    partFileUuid,
    isLegacyDxfViewer,
  }: SetLegacyDxfViewerForPartFilePayload) => ({
    type: types.SET_LEGACY_DXF_VIEWER_FOR_PART_FILE,
    payload: { partFileUuid, isLegacyDxfViewer },
  }),
};
